import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Col, Row, Spin} from 'antd';
import {CarOutlined} from '@ant-design/icons';
import dayjs, {Dayjs} from 'dayjs';

import {useGetCarQuery} from '../../../../store/api/carsApi';
import {useAppSelector} from '../../../../hooks/useStore';
import {
    useLazyGetBookingEstimatedCostQuery,
    useLazyGetIsDeploymentAvailableQuery
} from '../../../../store/api/bookingsApi';
import {BookingMapMarkerInfo, LocationInfo} from '../../../../types/deploymentTypes';
import {BookingCostsType} from '../../../../types/bookingTypes';
import {BaseError} from '../../../../types/apiTypes';
import {Car} from '../../../../types/carTypes';

import BookingCostBreakDown from '../../../../components/billings/CostBreakdown/BookingCostBreakdown';
import PreBookingDetailsCard from '../../../../components/booking/PreBookingDetailsCard';
import DetailsScreenHeader from '../../../../components/detailsScreenHeader/DetailsScreenHeader';
import PageTitle from '../../../../components/page/PageTitle';
import {cardsGridColumn} from '../../../../assets/styles';
import {messageType, showMessages} from '../../../../helpers/message';
import './BookingMapCreate.less';

const BookingMapCreate = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [startedAt, setStartedAt] = React.useState<Dayjs | null>(null);
    const [endedAt, setEndedAt] = React.useState<Dayjs | null>(null);
    const [locationInput, setLocationInput] = React.useState('');
    const [availability, setAvailability] = React.useState('');
    const [ghostCar, setGhostCar] = React.useState<Car | null>(null);
    const [markerInfo, setMarkerInfo] = React.useState<BookingMapMarkerInfo | null>(null);
    const [locationInfo, setLocationInfo] = React.useState<LocationInfo | null>(null);
    const [bookingCosts, setBookingCosts] = React.useState<BookingCostsType>({
        taxes: [],
        costs: [],
        totalTax: 0,
        totalCostWithoutTax: 0
    });
    const membershipID = useAppSelector(state => state.user.membership)?.id;

    const {data: car, error: carError, isFetching: carIsFetching} = useGetCarQuery(location.state?.carID);

    const [getBookingEstimatedCost, {isFetching: costIsFetching}] = useLazyGetBookingEstimatedCostQuery();

    const [getIsDeploymentAvailable, {isFetching: availabilityIsFetching}] = useLazyGetIsDeploymentAvailableQuery();

    React.useEffect(() => {
        if (carError) showMessages((carError as BaseError).data?.message, messageType.error);
    }, [carError]);

    React.useEffect(() => {
        if (location.state !== null) {
            // Start and End are sent through navigate/location.state as strings, as dayjs objs don't survive a refresh.
            if (location.state.startedAt !== null) setStartedAt(dayjs(location.state.startedAt));
            if (location.state.endedAt !== null) setEndedAt(dayjs(location.state.endedAt));

            setLocationInput(location.state.locationInput);
            setMarkerInfo(location.state.markerInfo);
            setLocationInfo(location.state.locationInfo);
        }
    }, [location]);

    React.useEffect(() => {
        const fetchDeploymentAvailability = async () => {
            if (markerInfo?.properties.deployments?.length && startedAt && endedAt) {
                // Get accurate deploymentID of current car.
                const deployment = markerInfo.properties.deployments.find(car => car.id === location.state.carID);
                if (!deployment) return;
                try {
                    const response = await getIsDeploymentAvailable({
                        deploymentID: deployment.id,
                        startTime: startedAt,
                        endTime: endedAt,
                    }).unwrap();
                    setAvailability(response);
                } catch (error) {
                    showMessages((error as BaseError).data?.message, messageType.error);
                }
            }
        };
        fetchDeploymentAvailability();
    }, [startedAt, endedAt, getIsDeploymentAvailable, markerInfo, location.state.carID]);

    React.useEffect(() => {
        const getEstimatedCost = async () => {
            if (startedAt && endedAt) {
                try {
                    const response = await getBookingEstimatedCost({
                        startTime: startedAt,
                        endTime: endedAt,
                        membershipID
                        // Something changed on the B/E that expects deployment_id as required now.
                    }).unwrap();
                    const {transformedCosts, originalCosts} = response;

                    setBookingCosts({
                        costs: Object.values(originalCosts),
                        ...transformedCosts,
                    });
                } catch (error) {
                    showMessages((error as BaseError).data?.message, messageType.error);
                }
            }
        };
        getEstimatedCost();
    }, [getBookingEstimatedCost, membershipID, startedAt, endedAt]);

    React.useEffect(() => {
        if (!locationInfo || !car) return;
        // Ghost car refers to a car that exists at a different location within the request's time range that has not moved there yet.
        if (locationInfo.location_id !== car.location_id) {
            setGhostCar({
                ...car,
                location_description: `${locationInfo.name} ${locationInfo.description}`,
                location_id: locationInfo.location_id,
            });
        }
    }, [car, locationInfo]);

    const handleClickBack = () => {
        // Retain state for the bookingMap inputs.
        const backToMap = {
            // Start/end can be null, check before converting to strings.
            startedAt: startedAt?.toString(),
            endedAt: endedAt?.toString(),
            markerInfo: markerInfo,
            locationInput: locationInput,
        }
        navigate('/booking/map', {state: backToMap});
    }

    return (
        carIsFetching ? <Spin/> :
            <Spin spinning={carIsFetching || availabilityIsFetching || costIsFetching}>
                <PageTitle title="Vehicle Information" icon={<CarOutlined/>}/>
                <DetailsScreenHeader
                    bookingNumber={0}
                    locationID={locationInfo?.location_id}
                    car={ghostCar || car}
                    onClickBack={handleClickBack}
                >
                    <Col xs={24} md={8} className="booking-button-wrapper">
                        <Button
                            // onClick={}
                            disabled={availability !== 'AVAILABLE'}
                            type="primary"
                        >
                            Book Vehicle
                        </Button>
                    </Col>
                </DetailsScreenHeader>
                <Row gutter={16}>
                    {/*add margin on each column to separate 2 columns when collapse*/}
                    <Col {...cardsGridColumn} style={{marginBottom: '20px'}}>
                        <PreBookingDetailsCard
                            startedAt={startedAt}
                            setStartedAt={setStartedAt}
                            endedAt={endedAt}
                            setEndedAt={setEndedAt}
                            availability={!availability ? 'N/A' : availability}
                            returnType="Set"
                        />
                    </Col>
                    <Col {...cardsGridColumn} style={{marginBottom: '20px'}}>
                        <BookingCostBreakDown
                            costs={bookingCosts.costs}
                            totalCost={bookingCosts.totalCostWithoutTax}
                            taxes={bookingCosts.taxes}
                            totalTax={bookingCosts.totalTax}
                            isEstimatedCost={true}
                            transactionsTable={false}
                        />
                    </Col>
                </Row>
            </Spin>
    );
}

export default BookingMapCreate;
